import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { ICTTextParagraphProperties, DeltaOf } from '@tencent/slide-sdk-model';
import { IParagraphCharacter } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { numToPercent, percentToNum, ptToPx, pxToPt } from '@src/slide/infrastructure/module/independent/unit';
import { BlipFillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/blip-fill/blip-fill-converter';
import { TEXT_AUTO_NUMBER_SCHEME_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/text-auto-number-scheme';
import _set from 'lodash/set';
import { invertMap } from '@src/slide/infrastructure/module/independent/object/map-invert';
import {
    ColorConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/color/color-converter';
import { IEGColorChoice } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';

/**
 * 文本段落属性 - 项目符号
 */
export class TextParagraphCharacterConverter {
    /**
     * 项目符号属性的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphCharacter,
    ): void {
        // 颜色
        TextParagraphCharacterConverter.convertColorSdkToUi(sdkData, uiData);
        // 字体
        TextParagraphCharacterConverter.convertFontSdkToUi(sdkData, uiData);
        // 大小
        TextParagraphCharacterConverter.convertSizeSdkToUi(sdkData, uiData);
        // 自定义图标
        TextParagraphCharacterConverter.convertBulletCharSdkToUi(sdkData, uiData);
        // 自定义图片
        TextParagraphCharacterConverter.convertBlipSdkToUi(sdkData, uiData);
        // 编号
        TextParagraphCharacterConverter.convertAutoNumSdkToUi(sdkData, uiData);
        // 无
        TextParagraphCharacterConverter.convertNoneSdkToUi(sdkData, uiData);
    }

    /**
     * 项目符号属性的ui -> sdk 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphCharacter>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        // 颜色
        TextParagraphCharacterConverter.convertColorUiToSdk(uiData, sdkData);
        // 字体
        TextParagraphCharacterConverter.convertFontUiToSdk(uiData, sdkData);
        // 大小
        TextParagraphCharacterConverter.convertSizeUiToSdk(uiData, sdkData);
        // 自定义图标
        TextParagraphCharacterConverter.convertBulletCharUiToSdk(uiData, sdkData);
        // 自定义图片
        TextParagraphCharacterConverter.convertBlipUiToSdk(uiData, sdkData);
        // 编号
        TextParagraphCharacterConverter.convertAutoNumUiToSdk(uiData, sdkData);
        // 无
        TextParagraphCharacterConverter.convertNoneUiToSdk(uiData, sdkData);
    }

    /**
     * 颜色
     * @param sdkData
     * @param uiData
     */
    private static convertColorSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphCharacter,
    ): void {
        // 项目符号颜色
        if (sdkData.buClr) {
            // uiData.buClr = {
            //     color: new TDColor({ rgbaStr: getEgColorChoiceRgba(sdkData.buClr.srgbClr) }),
            // };
            const tdColor = ColorConverter.convertPartialDataFromSdkToUi(sdkData.buClr);
            uiData.buClr = {
                color: tdColor,
            };
        }
        // 是否跟随后面的textRun颜色（优先级高于buClr）
        if (sdkData.buClrTx === true || sdkData.buClrTx === false) {
            uiData.buClrTx = sdkData.buClrTx ? 1 : 0;
        }
    }

    /**
     * 字体
     * @param sdkData
     * @param uiData
     */
    private static convertFontSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphCharacter,
    ): void {
        // 项目符号字体
        if (sdkData.buFont) {
            uiData.buFont = {
                typeface: sdkData.buFont.typeface,
                panose: sdkData.buFont.panose,
                pitchFamily: sdkData.buFont.pitchFamily,
                charset: sdkData.buFont.charset,
            };
        }
        // 是否跟随后面的textRun字体（优先级高于buFont）
        if (sdkData.buFontTx === true || sdkData.buClrTx === false) {
            uiData.buFontTx = sdkData.buFontTx ? 1 : 0;
        }
    }

    /**
     * 大小
     * @param sdkData
     * @param uiData
     */
    private static convertSizeSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphCharacter,
    ): void {
        // 项目符号大小
        if (sdkData.buSzPct?.val) {
            uiData.buSzPct = percentToNum(sdkData.buSzPct.val);
        }
        if (sdkData.buSzPts?.val) {
            uiData.buSzPts = ptToPx(sdkData.buSzPts.val);
        }
        // 是否跟随后面的textRun大小
        if (sdkData.buSzTx === true || sdkData.buSzTx === false) {
            uiData.buSzTx = sdkData.buSzTx ? 1 : 0;
        }
    }

    /**
     * 自定义图标
     * @param sdkData
     * @param uiData
     */
    private static convertBulletCharSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphCharacter,
    ): void {
        if (sdkData.buChar?.charKeyword) {
            uiData.character = true;
            uiData.character_bullet_char = sdkData.buChar.charKeyword;
        }
    }

    /**
     * 自定义图片
     * @param sdkData
     * @param uiData
     */
    private static convertBlipSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphCharacter,
    ): void {
        if (sdkData.buBlip?.blip?.embed) {
            uiData.character = true;
            uiData.buBlip = { blip: BlipFillConverter.convertPartialDataFromSdkToUi(sdkData.buBlip) };
        }
    }

    /**
     * 编号
     * @param sdkData
     * @param uiData
     */
    // todo itemSymbol_4对不上
    private static convertAutoNumSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphCharacter,
    ): void {
        if (sdkData.buAutoNum?.type) {
            uiData.character = true;
            uiData.buAutoNum = { type: TEXT_AUTO_NUMBER_SCHEME_MAP.get(sdkData.buAutoNum.type)! };
            if (sdkData.buAutoNum.startAt || sdkData.buAutoNum.startAt === 0) {
                uiData.buAutoNum.startAt = sdkData.buAutoNum.startAt;
            }
        }
    }

    /**
     * 无
     * @param sdkData
     * @param uiData
     */
    private static convertNoneSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphCharacter,
    ): void {
        if (sdkData.buNone === true || sdkData.buNone === false) {
            // 以下为迁移的注释
            // 看其他地方对 buNone 属性的注释，buNone === 0 表示有项目符号，buNone === 1 表示没有项目符号
            // 看了下这个属性应该是存在了好几年了，属于历史逻辑，我就按照这个逻辑修改 bug 了，只有在 buNone === 0 时才设置 character 为 true
            if (!sdkData.buNone) {
                uiData.character = true;
            }
            uiData.buNone = sdkData.buNone ? 1 : 0;
        }
    }

    /**
     * 颜色
     * @param uiData
     * @param sdkData
     */
    private static convertColorUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphCharacter>>,
        sdkData: DeltaOf<ICTTextParagraphProperties>,
    ): void {
        // 项目符号颜色
        const color = uiData.buClr?.color;
        const egColorChoice: IEGColorChoice = {};
        if (color) {
            ColorConverter.convertPartialDataFromUiToSdk(uiData.buClr?.color, egColorChoice);
            _set(sdkData, 'buClr', egColorChoice);
        } else {
            // 切为自动
            sdkData.buClr = undefined;
        }
        // 是否跟随后面的textRun颜色（优先级高于buClr）
        if (uiData.buClrTx === 1 || uiData.buClrTx === 0) {
            sdkData.buClrTx = !!uiData.buClrTx;
        }
    }

    /**
     * 字体
     * @param uiData
     * @param sdkData
     */
    private static convertFontUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphCharacter>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        // 项目符号字体
        if (uiData.buFont) {
            sdkData.buFont = {
                typeface: uiData.buFont.typeface,
                panose: uiData.buFont.panose,
                pitchFamily: uiData.buFont.pitchFamily,
                charset: uiData.buFont.charset,
            };
        }
        // 是否跟随后面的textRun字体（优先级高于buFont）
        if (uiData.buFontTx === 1 || uiData.buClrTx === 0) {
            sdkData.buFontTx = !!uiData.buFontTx;
        }
    }

    /**
     * 大小
     * @param uiData
     * @param sdkData
     */
    private static convertSizeUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphCharacter>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        // 项目符号大小
        if (uiData.buSzPct) {
            _set(sdkData, 'buSzPct.val', String(Math.round(numToPercent(uiData.buSzPct))));
        }
        if (uiData.buSzPts) {
            _set(sdkData, 'buSzPts.val', pxToPt(uiData.buSzPts));
        }
        // 是否跟随后面的textRun大小
        if (uiData.buSzTx === 1 || uiData.buSzTx === 0) {
            sdkData.buSzTx = !!uiData.buSzTx;
        }
    }

    /**
     * 自定义图标
     * @param uiData
     * @param sdkData
     */
    private static convertBulletCharUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphCharacter>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.character_bullet_char) {
            _set(sdkData, 'buChar.charKeyword', uiData.character_bullet_char);
        }
    }

    /**
     * 自定义图片
     * @param uiData
     * @param sdkData
     */
    private static convertBlipUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphCharacter>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.buBlip?.blip) {
            sdkData.buBlip = BlipFillConverter.convertPartialDataFromUiToSdk(uiData.buBlip.blip);
        }
    }

    /**
     * 编号
     * @param uiData
     * @param sdkData
     */
    private static convertAutoNumUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphCharacter>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.buAutoNum?.type) {
            _set(sdkData, 'buAutoNum.type', invertMap(TEXT_AUTO_NUMBER_SCHEME_MAP).get(uiData.buAutoNum.type));
        }
        if (uiData.buAutoNum?.startAt || uiData.buAutoNum?.startAt === 0) {
            _set(sdkData, 'buAutoNum.startAt', uiData.buAutoNum.startAt);
        }
    }

    /**
     * 无
     * @param uiData
     * @param sdkData
     */
    private static convertNoneUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphCharacter>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.buNone === 1 || uiData.buNone === 0) {
            sdkData.buNone = !!uiData.buNone;
        }
    }
}
